import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import MenuItem from "@mui/material/MenuItem";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import WarningIcon from "@mui/icons-material/Warning";
import { red } from "@mui/material/colors";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { InputAdornment } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MDButton from "components/MDButton";
import Swal from "sweetalert2";
import moment from "moment";
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";
import api from "core/interceptors/api";
import { disableBackNavigation } from "core/utils/storage";
import { validateEmail, validatePassword } from "../../validations";

const columns = [
    {
        Header: "S.No.",
        accessor: "sno",
        sortable: true,
    },
    {
        Header: "Email",
        accessor: "email",
        sortable: true,
    },
    {
        Header: "Password",
        accessor: "password",
        sortable: true,
    },
    {
        Header: "Joining Date",
        accessor: "joining_date",
        sortable: true,
    },
    {
        Header: "Referral Code",
        accessor: "referral_code",
        sortable: true,
    },

    {
        Header: "Status",
        accessor: "status",
        sortable: true,
    },
    {
        Header: "Options",
        accessor: "option",
    },
];

function AdminRegister() {

    const [roleFilter, setRoleFilter] = useState("");

    const [openAddModal, setOpenAddModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [Input, setInput] = useState({});
    const [editInput, setEditInput] = useState({});
    const [loading, setLoading] = useState(true);
    const [editadminId, setEditadminId] = useState(null);
    const [deleteadminId, setDeleteadminId] = useState(null);
    const [addValidationErrors, setAddValidationErrors] = useState({});
    const [editValidationErrors, setEditValidationErrors] = useState({});
    const [admins, setAdmins] = useState([]);
    const email = localStorage.getItem("email");
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [showEditPassword, setShowEditPassword] = useState(false);

    // Pagination states
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // State for global search
    const [searchQuery, setSearchQuery] = useState('');

    const handleClose = () => {
        setOpenEditModal(false);
        setOpenDeleteModal(false);
        setOpenAddModal(false);
        setAddValidationErrors({}); // Clear validation errors on close
        setEditValidationErrors({}); // Clear validation errors on close
        setInput({}); // Clear input fields
        setEditInput({}); // Clear edit input fields
    };

    const togglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    const toggleEditPasswordVisibility = () => {
        setShowEditPassword((prev) => !prev);
    };

    useEffect(() => {
        disableBackNavigation();
        fetchAdmins();
    }, []);

    const fetchAdmins = async () => {
        try {
            const res = await api.get("/login/all", {
                params: { email },
            });

            // Filter out users with role "Super Admin"
            const filteredAdmins = (res.data || []).filter(admin => admin.role !== "Super Admin");

            setAdmins(filteredAdmins);
        } catch (error) {
            console.error("Error fetching admins:", error);
        } finally {
            setLoading(false);
        }
    };


    const isDuplicateEmail = (emailToCheck, currentAdminId = null) => {
        return admins.some(
            (admin) =>
                admin.email.toLowerCase() === emailToCheck.toLowerCase() &&
                admin.id !== currentAdminId
        );
    };

    const handleAddChange = (e) => {
        const { name, value } = e.target;
        const updatedInput = { ...Input, [name]: value };
        setInput(updatedInput);

        let error = "";
        if (name === "email") {
            error = validateEmail(value);
            if (!error && isDuplicateEmail(value)) {
                error = "This email is already registered.";
            }
        } else if (name === "password") {
            error = validatePassword(value);
        }
        setAddValidationErrors((prev) => ({ ...prev, [name]: error }));
    };
    const checkEmailInOtherRoles = async (email) => {
        try {
            const res = await api.post("/check_email_roles", { email });
            return res.data; // { exists: boolean, roles: [] }
        } catch (err) {
            console.error("Email role check failed:", err);
            return { exists: false, roles: [] };
        }
    };

    const addAdmin = async (event) => {
        event.preventDefault();

        const emailError = validateEmail(Input.email);
        const passwordError = validatePassword(Input.password);
        const duplicateAdminError = isDuplicateEmail(Input.email)
            ? "This email is already registered as Admin/Staff."
            : "";

        const errors = {};
        if (emailError) errors.email = emailError;
        else if (duplicateAdminError) errors.email = duplicateAdminError;
        if (passwordError) errors.password = passwordError;
        if (!Input.role) errors.role = "Role is required.";

        setAddValidationErrors(errors);
        if (Object.keys(errors).length > 0) return;

        // 🔍 CHECK EMAIL IN VENDOR / SERVICE PROVIDER
        const emailCheck = await checkEmailInOtherRoles(Input.email);

        if (emailCheck.exists) {
            const roleNames = emailCheck.roles
                .map((r) =>
                    r === "vendor"
                        ? "Vendor"
                        : r === "service_provider"
                            ? "Service Provider"
                            : r
                )
                .join(" & ");

            const result = await Swal.fire({
                icon: "warning",
                title: "Email Already Used",
                html: `
    <p>This email is already registered as <b>${roleNames}</b>.</p>
    <p>You can still create Admin/Staff using the same email.</p>
    <p><b>Do you want to continue?</b></p>
  `,
                showCancelButton: true,
                confirmButtonText: "Yes, Continue",
                cancelButtonText: "Cancel",
                confirmButtonColor: "#2e7d32",
                cancelButtonColor: "#d33",
                didOpen: () => {
                    document.querySelector('.swal2-container').style.zIndex = 2000;
                }
            });


            if (!result.isConfirmed) return; // ❌ Stop admin creation
        }

        // ✅ CREATE ADMIN
        try {
            const response = await api.post("/login/create", {
                email: Input.email,
                password: Input.password,
                role: Input.role,
            });

            if (response.data.status === 1) {
                Swal.fire({
                    title: "Admin/Staff Added Successfully!",
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false,
                });
                fetchAdmins();
                setPage(0);
                setOpenAddModal(false);
                setInput({});
            } else {
                Swal.fire("Error", response.data.message || "Server error", "error");
            }
        } catch (error) {
            console.error("Add admin error:", error);
            Swal.fire(
                "Error",
                error.response?.data?.message || "Unexpected error occurred",
                "error"
            );
        }
    };


    const deleteAdmin = async (event) => {
        event.preventDefault();
        console.log("admin ID : ", deleteadminId);
        if (deleteadminId) {
            try {
                const response = await api.delete(
                    `login/delete/${deleteadminId}`
                );
                console.log(response.data.success);
                setOpenDeleteModal(false);
                if (response.data.status === 1) {
                    Swal.fire({
                        icon: "success",
                        text: "Successfully deleted...",
                        type: "success",
                        timer: 2000,
                        showConfirmButton: false,
                    });
                    fetchAdmins();
                    setPage(0); // Reset page to 0 after deleting an admin
                } else {
                    Swal.fire({
                        icon: "error",
                        text:
                            response.data.message || "Server Problem. Please Try Again...",
                        type: "error",
                        timer: 2000,
                        showConfirmButton: false,
                    });
                }
            } catch (error) {
                console.error("Error deleting admin:", error);
                Swal.fire({
                    icon: "error",
                    text: "An unexpected error occurred. Please try again.",
                    type: "error",
                    timer: 2000,
                    showConfirmButton: false,
                });
            }
        } else {
            console.error("No admin ID provided for deletion.");
        }
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        const updatedInput = { ...editInput, [name]: value };
        setEditInput(updatedInput);

        let error = "";
        if (name === "email") {
            error = validateEmail(value);
            if (!error && isDuplicateEmail(value, editadminId)) {
                error = "This email is already registered.";
            }
        } else if (name === "password") {
            error = validatePassword(value);
        }
        setEditValidationErrors((prev) => ({ ...prev, [name]: error }));
    };

    const updateAdmin = async (event) => {
        event.preventDefault();

        const emailError = validateEmail(editInput.email);
        const passwordError = validatePassword(editInput.password);
        const duplicateError = isDuplicateEmail(editInput.email, editadminId)
            ? "This email is already registered."
            : "";

        const errors = {};
        if (emailError) errors.email = emailError;
        else if (duplicateError) errors.email = duplicateError;

        if (passwordError) errors.password = passwordError;

        setEditValidationErrors(errors);

        if (Object.keys(errors).length > 0) return;

        try {
            const response = await api.put(`/login/update/${editadminId}`, {
                email: editInput.email,
                password: editInput.password,
            });

            if (response.data.status === 1) {
                Swal.fire({
                    title: "Successfully Updated!",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 1500,
                });
                fetchAdmins();
                setOpenEditModal(false);
                setEditInput({});
            } else {
                Swal.fire({
                    icon: "error",
                    text: response.data.message || "Failed to update admin",
                    timer: 2000,
                    showConfirmButton: false,
                });
            }
        } catch (error) {
            console.error("Error updating Admin:", error);
            Swal.fire({
                title: "Error",
                text: "An unexpected error occurred. Please try again.",
                icon: "error",
            });
        }
    };

    // Filter admins based on search query
    const filteredAdmins = useMemo(() => {
        let result = admins;

        // ➜ role dropdown
        if (roleFilter) {
            result = result.filter((a) => a.role === roleFilter);
        }

        // ➜ existing free‑text search
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter(
                (a) =>
                    a.email.toLowerCase().includes(q) ||
                    a.status.toLowerCase().includes(q)
            );
        }

        return result;
    }, [admins, roleFilter, searchQuery]);

    // Adjust page if current page is out of bounds after filtering
    useEffect(() => {
        const totalFilteredItems = filteredAdmins.length;
        if (page * rowsPerPage >= totalFilteredItems && totalFilteredItems > 0) {
            setPage(0); // Go to first page if current page is now empty
        } else if (totalFilteredItems === 0 && page !== 0) {
            setPage(0); // If no items, ensure page is 0
        }
    }, [page, rowsPerPage, filteredAdmins]);


    // Calculate the subset of data for the current page from filteredAdmins
    const startIndex = page * rowsPerPage;
    const paginatedAdmins = filteredAdmins.slice(startIndex, startIndex + rowsPerPage);

    // Handlers for pagination (These were causing the "not defined" error when commented out)
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event, newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0); // Reset to the first page when rows per page changes
    };

    const data = paginatedAdmins.map((admin, index) => ({
        sno: startIndex + index + 1,
        id: admin.id,
        email: admin.email,
        password: admin.password,
        role: admin.role,
        joining_date: admin.created_at
            ? moment(admin.created_at).format("DD-MM-YYYY")
            : "N/A",
        referral_code: admin.code || "N/A",
        status: admin.status,

        option: (
            <div style={{ display: "flex" }}>
                <IconButton
                    color="success"
                    size="sm"
                    onClick={() => {
                        setOpenEditModal(true);
                        setEditInput({
                            id: admin.id,
                            email: admin.email,
                            password: admin.password,
                            role: admin.role,
                            status: admin.status,
                        });
                        setEditadminId(admin.id);
                        // Re-validate edit input on opening the modal to ensure correct initial state of errors
                        setEditValidationErrors({
                            email: validateEmail(admin.email) || (isDuplicateEmail(admin.email, admin.id) ? "This email is already registered." : ""),
                            password: validatePassword(admin.password),
                        });
                    }}
                >
                    <EditIcon />
                </IconButton>
                <IconButton
                    color="error"
                    size="sm"
                    style={{ marginLeft: "5px" }}
                    onClick={() => {
                        setDeleteadminId(admin.id);
                        setOpenDeleteModal(true);
                    }}
                >
                    <DeleteIcon />
                </IconButton>
            </div>
        ),
    }));

    // This console log isn't directly related to the component's functionality,
    // but keeping it as it was in the original code.
    const formattedTime = moment(editInput.schedule_time, "HH:mm:ss").format(
        "HH:mm:ss"
    );
    // console.log("Formatted Time:", formattedTime);

    return (
        <DashboardLayout>
            <MDBox display="flex" justifyContent="flex-end" alignItems="center">
                <LogoutButton
                    onLogout={() => {
                        navigate("/login");
                    }}
                />
            </MDBox>
            <DashboardNavbar />

            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                            >
                                <MDTypography variant="h6" color="white">
                                    Admin /Staff Register
                                </MDTypography>
                            </MDBox>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={2} mr={4} ml={4}>
                                {/* 🔍 Free‑text search (kept as‑is) */}
                                <TextField
                                    label="Search"
                                    variant="outlined"
                                    size="small"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    sx={{ width: 220 }}
                                />

                                <MDBox display="flex" flexDirection="column" sx={{ mx: 2 }}>
                                    <MDTypography variant="caption" fontWeight="bold" mb={0.5}>
                                        Filter by Roles
                                    </MDTypography>
                                    <TextField
                                        select
                                        value={roleFilter}
                                        onChange={(e) => setRoleFilter(e.target.value)}
                                        size="medium"
                                        sx={{
                                            width: 220,
                                            height: 56, // Adjusted height
                                            ".MuiInputBase-root": {
                                                height: 48, // Inner input height
                                                display: "flex",
                                                alignItems: "center",
                                            },
                                        }}
                                        displayEmpty
                                        defaultValue=""
                                        SelectProps={{
                                            displayEmpty: true,
                                            MenuProps: {
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 300,
                                                    },
                                                },
                                            },
                                        }}
                                    >

                                        <MenuItem value="">All Roles</MenuItem>
                                        <MenuItem value="Admin">Admin</MenuItem>
                                        <MenuItem value="Marketing Manager">Marketing Manager</MenuItem>
                                        <MenuItem value="Telecaller">Telecaller</MenuItem>
                                    </TextField>
                                </MDBox>



                                <MDButton color="success" onClick={() => {
                                    setOpenAddModal(true);
                                    setInput({}); // Clear input on opening Add modal
                                    setAddValidationErrors({}); // Clear validation errors on opening Add modal
                                }}>
                                    Add admin/Staff
                                </MDButton>
                            </MDBox>

                            <Dialog open={openAddModal} onClose={handleClose} fullWidth maxWidth="md">
                                <DialogTitle>Add Admin/Staff</DialogTitle>
                                <MDBox component="form">
                                    <DialogContent>
                                        <Grid container spacing={2}>
                                            {/* Email */}
                                            <Grid item xs={12} sm={6}>
                                                <MDBox>
                                                    <TextField
                                                        label="Email"
                                                        type="email"
                                                        name="email"
                                                        value={Input.email || ""}
                                                        onChange={handleAddChange}
                                                        fullWidth
                                                        margin="normal"
                                                        error={!!addValidationErrors.email}
                                                        helperText={addValidationErrors.email}
                                                    />
                                                </MDBox>
                                            </Grid>

                                            {/* Password */}
                                            <Grid item xs={12} sm={6}>
                                                <MDBox>
                                                    <TextField
                                                        label="Password"
                                                        type={showPassword ? "text" : "password"}
                                                        name="password"
                                                        value={Input.password || ""}
                                                        onChange={handleAddChange}
                                                        fullWidth
                                                        margin="normal"
                                                        error={!!addValidationErrors.password}
                                                        helperText={addValidationErrors.password}
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton onClick={togglePasswordVisibility} edge="end">
                                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </MDBox>
                                            </Grid>

                                            {/* Role (Readonly - Always "Admin") */}
                                            <Grid item xs={12} sm={6}>
                                                <MDBox>
                                                    <TextField
                                                        select
                                                        label="Role"
                                                        name="role"
                                                        value={Input.role || ""}
                                                        onChange={handleAddChange}
                                                        fullWidth
                                                        margin="normal"
                                                        error={!!addValidationErrors.role}
                                                        helperText={addValidationErrors.role}
                                                        size="medium"  // ensures default medium size
                                                        SelectProps={{
                                                            sx: {
                                                                minHeight: 44,
                                                                paddingTop: "12px",
                                                                paddingBottom: "12px",
                                                            },
                                                        }}
                                                        sx={{
                                                            "& .MuiInputBase-root": {
                                                                minHeight: 44,
                                                                alignItems: "center", // vertically center content inside the select
                                                            },
                                                        }}
                                                    >
                                                        {["Admin", "Marketing Manager", "Telecaller"].map((roleOption) => (
                                                            <MenuItem key={roleOption} value={roleOption}>
                                                                {roleOption}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </MDBox>
                                            </Grid>

                                        </Grid>
                                    </DialogContent>
                                </MDBox>

                                {/* Action Buttons */}
                                <DialogActions>
                                    <Button onClick={handleClose} color="primary">
                                        Cancel
                                    </Button>
                                    <MDButton type="button" color="success" onClick={addAdmin}
                                        disabled={
                                            !Input.email ||
                                            !Input.password ||
                                            !!addValidationErrors.email ||
                                            !!addValidationErrors.password
                                        }
                                    >
                                        Save
                                    </MDButton>
                                </DialogActions>
                            </Dialog>

                            {/* Edit Modal */}
                            <Dialog
                                open={openEditModal}
                                onClose={handleClose}
                                fullWidth
                                maxWidth="md"
                            >
                                <DialogTitle>Edit Admin</DialogTitle>
                                <MDBox component="form">
                                    <DialogContent>
                                        <Grid container spacing={2}>
                                            {/* Email */}
                                            <Grid item xs={12} sm={6}>
                                                <MDBox>
                                                    <TextField
                                                        label="Email"
                                                        type="email"
                                                        name="email"
                                                        value={editInput.email || ""}
                                                        onChange={handleEditChange}
                                                        fullWidth
                                                        margin="normal"
                                                        error={!!editValidationErrors.email}
                                                        helperText={editValidationErrors.email}
                                                    />
                                                </MDBox>
                                            </Grid>

                                            {/* Password */}
                                            <Grid item xs={12} sm={6}>
                                                <MDBox>
                                                    <TextField
                                                        label="Password"
                                                        type={showEditPassword ? "text" : "password"}
                                                        name="password"
                                                        value={editInput.password || ""}
                                                        onChange={handleEditChange}
                                                        fullWidth
                                                        margin="normal"
                                                        error={!!editValidationErrors.password}
                                                        helperText={editValidationErrors.password}
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton onClick={toggleEditPasswordVisibility} edge="end">
                                                                        {showEditPassword ? <VisibilityOff /> : <Visibility />}
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </MDBox>
                                            </Grid>
                                        </Grid>
                                    </DialogContent>
                                </MDBox>

                                {/* Action Buttons */}
                                <DialogActions>
                                    <Button onClick={handleClose} color="primary">
                                        Cancel
                                    </Button>
                                    <MDButton
                                        color="success"
                                        type="button"
                                        onClick={updateAdmin}
                                        disabled={
                                            !editInput.email ||
                                            !editInput.password ||
                                            !!editValidationErrors.email ||
                                            !!editValidationErrors.password
                                        }
                                    >
                                        Save
                                    </MDButton>
                                </DialogActions>
                            </Dialog>


                            {/* Delete Modal */}
                            <Dialog open={openDeleteModal} onClose={handleClose}>
                                <MDBox component="form" onSubmit={deleteAdmin}>
                                    <DialogTitle>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item>
                                                <WarningIcon
                                                    style={{ color: red[700], fontSize: 40 }}
                                                />
                                            </Grid>
                                            <Grid item>
                                                <MDTypography variant="h6" component="span">
                                                    Confirm Delete
                                                </MDTypography>
                                            </Grid>
                                        </Grid>
                                    </DialogTitle>

                                    <DialogContent>
                                        <MDBox
                                            p={2}
                                            style={{
                                                backgroundColor: red[50],
                                                borderLeft: `5px solid ${red[700]}`,
                                            }}
                                        >
                                            <MDTypography variant="body1">
                                                Are you sure you want to delete this item?.
                                            </MDTypography>
                                        </MDBox>
                                    </DialogContent>
                                    <DialogActions>
                                        <MDButton onClick={handleClose} style={{ color: "gray" }}>
                                            Cancel
                                        </MDButton>
                                        <MDButton type="submit" color="primary">
                                            Delete
                                        </MDButton>
                                    </DialogActions>
                                </MDBox>
                            </Dialog>

                            <MDBox p={3}>
                                <DataTable
                                    table={{ columns: columns, rows: data }}
                                    isSorted={true}
                                    entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }}
                                    showTotalEntries={true}
                                    canSearch={false} // Disabled DataTable's internal search for global search
                                    noEndBorder
                                    pagination={{
                                        variant: "contained",
                                        color: "dark",
                                        page: page,
                                        onPageChange: handlePageChange,
                                        rowsPerPage: rowsPerPage,
                                        onRowsPerPageChange: handleRowsPerPageChange,
                                        count: filteredAdmins.length, // Count based on filtered data
                                    }}
                                />
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>


        </DashboardLayout>
    );
}

export default AdminRegister;